import { PHASE_GAME_OVER } from "../types.ts";
import {
  setPhase,
  loseLives,
  setLives,
  incrementRecursiveLoopCounter,
  resetRecursiveLoopCounter,
} from "../stateIntents.ts";
import { COLORS } from "../../core/constants.ts";
import { t } from "../../utils/i18n.ts";
import { RARITY_SLOT_COLORS } from "../../core/palette.ts";
import {
  CORE_HIT_FEEL,
  FEEL_BEATS,
  coreHitFlashAlpha,
  coreHitShakeAmp,
} from "../../core/feelBudget.ts";
import type { Element } from "../../entities/types.ts";
import type { CombatHandlerDeps } from "./types.ts";
import { killHitStopTier } from "../../rendering/DensityGlow.ts";

export type { HandlerState } from "./types.ts";

/** Wire combat-related EventBus subscriptions. */
export function setupCombatHandlers(deps: CombatHandlerDeps): (() => void)[] {
  const {
    eventBus,
    gs,
    particleSystem,
    screenEffects,
    draftSystem,
    projectilePool,
    gameLoop,
    state,
  } = deps;
  const unsubs: (() => void)[] = [];

  unsubs.push(
    eventBus.on("projectile.hit", (d: unknown) => {
      const ev = d as {
        target: { x: number; y: number };
        damage: number;
        projectile?: { damage?: number };
      };
      // S195/BL-281 (S195-F09): crits were computed sim-side and never told
      // the render layer — the ⚡ amber crit number existed as dead, unwired
      // vocabulary. Render-only BIG-HIT read: the payload carries both the
      // dealt damage and the projectile's base damage, so an outcome ≥ the
      // crit multiplier's worth of the base (2× — DamageCalculation.ts crit
      // ×2) reads as a crit/overload hit. Sim untouched; no new events.
      const base = ev.projectile?.damage ?? Number.POSITIVE_INFINITY;
      if (ev.damage >= base * 2 && Number.isFinite(base)) {
        deps.floatingDmg.spawnCrit(ev.target.x, ev.target.y - 10, ev.damage);
      } else {
        deps.floatingDmg.spawn(ev.target.x, ev.target.y - 10, ev.damage);
      }
    }),
  );

  unsubs.push(
    eventBus.on("enemy.killed", (d: unknown) => {
      gs.runStats.enemiesKilled++;
      const ev = d as {
        enemy: {
          x: number;
          y: number;
          weakness?: string;
          maxHp?: number;
          type?: string;
          pathIndex?: number;
        };
      };
      const killedAt = Math.max(0, ev.enemy.pathIndex ?? 0);
      deps.breachSystem.recordKillDistance(killedAt);
      particleSystem.emitKill(
        ev.enemy.x,
        ev.enemy.y,
        ev.enemy.weakness ?? "fire",
      );
      particleSystem.emitFlash(ev.enemy.x, ev.enemy.y);
      // S195/BL-279: the kill tier's screen shake is owned by CombatFeel (the
      // combo-scaled feelBudget entry) — the redundant 0.5 px pre-shake this
      // handler used to fire was pure dispatch noise (S195-F05).

      const enemyType = ev.enemy.type ?? "";
      if (enemyType !== "fast" && enemyType !== "swarm") {
        // BL-44 Part A: de-saturate the kill freeze. The §6.8 short freeze steps
        // down with live density (short → micro → none) so a kill-storm can't
        // perpetually refresh it (the "hängt total" bug); the GameLoop min-gap
        // throttles back-to-back refreshes too. board-reveal (main.ts) + payload
        // (PayloadDetonationFx) call hitStopTier directly and stay priority — a
        // higher tier interrupts the cooldown. Count is O(1) (enemyPool.active).
        const tier = killHitStopTier(deps.waveManager.enemies.length);
        // BL-44 rung 1: the governor suppresses the kill freeze entirely under
        // measured load (the cheapest, highest-value lever — §1).
        if (tier !== "none" && !deps.perfGovernor.shouldSuppressHitStop()) {
          gameLoop.hitStopTier(tier);
        }
      }

      if (gs.recursiveLoopThreshold > 0 && !state.recursiveLoopProcessing) {
        incrementRecursiveLoopCounter(gs);
        if (gs.recursiveLoopCounter >= gs.recursiveLoopThreshold) {
          resetRecursiveLoopCounter(gs);
          state.recursiveLoopProcessing = true;
          const shrapnelDmg = Math.floor((ev.enemy.maxHp ?? 100) * 0.25);
          const killed: (typeof deps.waveManager.enemies)[number][] = [];
          for (const enemy of deps.waveManager.enemies) {
            if (!enemy.active) continue;
            const wasKilled = enemy.takeDamage(shrapnelDmg, "lightning");
            if (wasKilled) {
              enemy.active = false;
              killed.push(enemy);
            }
          }
          for (const enemy of killed) {
            eventBus.emit("enemy.killed", { enemy });
          }
          state.recursiveLoopProcessing = false;
          screenEffects.flash(COLORS.AMBER_WARN, 0.3);
        }
      }
    }),
  );

  unsubs.push(
    eventBus.on("enemy.reachedEnd", (d: unknown) => {
      const ev = d as { enemy?: { livesDamage?: number } } | undefined;
      const damage = ev?.enemy?.livesDamage ?? 1;
      if (gs.phase === PHASE_GAME_OVER || state.runEndFired) return;
      state.leaksThisWave++;
      loseLives(gs, damage);
      deps.gridRenderer.mainframe.triggerHit();
      deps.gridRenderer.mainframe.setHP(gs.lives, 20);
      eventBus.emit("mainframe.hit", { damage, livesLeft: gs.lives });
      screenEffects.shake(coreHitShakeAmp(damage), CORE_HIT_FEEL.shakeMs);
      screenEffects.flash(COLORS.MALWARE_RED, coreHitFlashAlpha(damage));
      screenEffects.vignette(COLORS.MALWARE_RED, CORE_HIT_FEEL.vignetteMs);
      if (gs.lives <= 0) {
        if (deps.failsafe.available) {
          setLives(gs, 1);
          deps.failsafe.consume();
          screenEffects.flash(COLORS.SENTINEL_GREEN, 0.5);
          eventBus.emit("failsafe.triggered");
          return;
        }
        setPhase(gs, PHASE_GAME_OVER);
        state.runEndFired = true;
        gameLoop.stopAfterFrames(2);
        eventBus.emit("game.over", {
          score: deps.scoreSystem.score,
          wave: gs.wave,
        });
        const currentScore = deps.scoreSystem.score;
        // Same population rule as `runEndHandler` — the player's best at THIS
        // threat level, never a flat best across all five (owner, 2026-08-16).
        const runLevel = deps.metaState?.settings.threatLevel;
        const previousBest =
          deps.metaState?.highscores
            .filter((h) => h.threatLevel === runLevel)
            .reduce((best, h) => Math.max(best, h.score), 0) ?? 0;
        const isHighscore = currentScore > previousBest;
        deps.gameOverScreen.show(
          currentScore,
          gs.wave,
          // Bug 16: in an endless run that began with a wave-35 win, show the
          // real campaign integrity bonus (stored on the win) instead of a
          // literal 0; a plain non-endless death keeps 0 (campaignLivesBonus=0).
          gs.campaignLivesBonus,
          () => {
            // CR-31: RESTART RUN — same operator/draft, fresh wave 1
            eventBus.emit("game.restart");
          },
          () => {
            // CR-31: MAIN MENU — back to the boot main menu (operator re-pick)
            eventBus.emit("game.returnToMenu");
          },
          gs.runStats,
          isHighscore,
          deps.metaState?.playerInitials,
        );
      }
    }),
  );

  unsubs.push(
    eventBus.on("projectile.hit", (d: unknown) => {
      const ev = d as {
        target: { x: number; y: number };
        projectile: { element: string };
      };
      particleSystem.emit(ev.target.x, ev.target.y, ev.projectile.element, 6);
    }),
  );

  // Virus infection-zone impact: ring tracing the AoE radius + central burst +
  // flash so the lobbed shell's area effect is clearly readable on landing.
  unsubs.push(
    eventBus.on("virus.impact", (d: unknown) => {
      const ev = d as { x: number; y: number; radius: number };
      particleSystem.emitRing(ev.x, ev.y, ev.radius, "virus", 18);
      particleSystem.emit(ev.x, ev.y, "virus", 8);
      particleSystem.emitFlash(ev.x, ev.y);
    }),
  );

  unsubs.push(
    eventBus.on("virus.hazard.tick", (d: unknown) => {
      const ev = d as { x: number; y: number };
      particleSystem.emit(ev.x, ev.y, "virus", 3);
    }),
  );

  unsubs.push(
    eventBus.on("synergy.triggered", (d: unknown) => {
      const ev = d as {
        target: { x: number; y: number };
        synergy?: { id: string };
        type?: string;
      };
      const id = ev.synergy?.id ?? ev.type ?? "steam";
      particleSystem.emitSynergy(ev.target.x, ev.target.y, id);
    }),
  );

  // F-14: Electroshock stun feedback — flash + sparks at the stunned enemy.
  unsubs.push(
    eventBus.on("synergy.stun", (d: unknown) => {
      const ev = d as { target: { x: number; y: number } };
      particleSystem.emitFlash(ev.target.x, ev.target.y);
      particleSystem.emitSynergy(ev.target.x, ev.target.y, "electroshock");
    }),
  );

  unsubs.push(
    eventBus.on("synergy.autoFire", (d: unknown) => {
      const ev = d as {
        x: number;
        y: number;
        target: import("../../entities/Enemy.ts").Enemy;
        damage: number;
        element: string;
      };
      const proj = projectilePool.acquire();
      proj.init(
        ev.x,
        ev.y,
        ev.target,
        300,
        ev.damage,
        ev.element as Element,
        "#ffffff",
      );
    }),
  );

  // S195/BL-280 (S195-F03): the per-threshold combo screen effects are
  // RETIRED — the raw-pink #ff44aa full-screen flash fired on every
  // non-%10 threshold (combo 2/5/35) as frequent low-value noise and
  // colour-stole the ×7 milestone frame; the %10 variants duplicated the
  // BL-241 milestone tier. Sub-milestone chains are carried by the on-board
  // ComboReadout; the milestone tier (main.ts, feelBudget) owns the screen.

  unsubs.push(
    eventBus.on("tower.placed", (d: unknown) => {
      gs.runStats.towersBuilt++;
      const ev = d as { tower: { x: number; y: number; element: string } };
      // BL-240 — the deploy "thunk": the existing element spray + a socket-snap
      // ring + a tiny screen-punch so a placement reads as a weighty commit, not
      // a silent appearance. Render-only (shake self-gates reduced motion/load).
      particleSystem.emit(ev.tower.x, ev.tower.y, ev.tower.element, 4);
      particleSystem.emitRing(ev.tower.x, ev.tower.y, 18, ev.tower.element, 12);
      screenEffects.shake(...FEEL_BEATS.towerPlace.shake);
    }),
  );

  unsubs.push(
    eventBus.on("enemy.spawned", (d: unknown) => {
      const ev = d as {
        enemy: {
          hp?: number;
          maxHp?: number;
          special?: { type?: string };
          applySlow: (f: number, dur: number) => void;
        };
      };
      const heavy = (ev.enemy.maxHp ?? ev.enemy.hp ?? 0) >= 500;
      if (heavy) {
        // S143 / BL-136 (PT-40 #1): the CORE BREAKER warning was firing per
        // spawn from ~W25 — banner + a full-screen red flash + a shake every
        // time a heavy crossed the line. Owner ask: appear ONCE per occurrence,
        // SILENT (no spawn sound on appearance), NO red flash. The W35 final
        // boss (special.type === "finalBoss") still earns its theatre — keep
        // the flash + shake for it; only the non-final HP-≥500 elites get
        // calmed.
        const specialType = ev.enemy.special?.type;
        const isFinalBoss = specialType === "finalBoss";
        if (isFinalBoss) {
          screenEffects.flash(COLORS.ORACLE_RED, 0.3);
          screenEffects.shake(2, 200);
          deps.gameBanner.show(
            t("warning.coreBreaker"),
            COLORS.ORACLE_RED,
            2000,
          );
        } else if (!state.coreBreakerShownThisWave) {
          // Once per wave, no flash, no shake. The banner alone reads as a
          // notice instead of an alarm.
          state.coreBreakerShownThisWave = true;
          deps.gameBanner.show(
            t("warning.coreBreaker"),
            COLORS.ORACLE_RED,
            2000,
          );
        }
      }
      const slow = draftSystem.hasGlobalSlow();
      if (slow < 1) ev.enemy.applySlow(slow, 999999);
    }),
  );

  unsubs.push(
    eventBus.on("draft.cardSelected", (d: unknown) => {
      const ev = d as { card?: { rarity?: string } };
      const rarity = ev?.card?.rarity ?? "common";
      // BL-116: the per-rarity flash hue IS the central rarity-slot palette
      // (this was a verbatim inline copy of RARITY_SLOT_COLORS).
      const flashColor = RARITY_SLOT_COLORS[rarity] ?? RARITY_SLOT_COLORS.common;
      screenEffects.flash(flashColor, 0.5);
      gameLoop.slowMo(180, 0.25);
      particleSystem.emit(
        480,
        336,
        "fire",
        rarity === "legendary" ? 30 : rarity === "epic" ? 20 : 10,
      );
    }),
  );

  return unsubs;
}
